import React, { useState, useEffect, useRef } from 'react';
import { TreePine } from 'lucide-react';
import { preloadImage, isImageCached } from '../utils/imageCache';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  width?: number;
  height?: number;
  onLoad?: () => void;
  quality?: number;
}

export default function ImageWithFallback({
  src,
  alt,
  className = '',
  priority = false,
  width,
  height,
  onLoad,
  quality = 75
}: ImageWithFallbackProps) {
  const [isLoading, setIsLoading] = useState(!isImageCached(src));
  const [error, setError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const imageRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // If image is prioritized or cached, load immediately
    if (priority || isImageCached(src)) {
      preloadImage(src)
        .then(() => {
          setIsLoading(false);
          onLoad?.();
        })
        .catch(() => {
          // Try alternate URL if fails
          if (currentSrc === src) {
            const altSrc = src.startsWith('/') ? src.substring(1) : `/${src}`;
            setCurrentSrc(altSrc);
          } else {
            setError(true);
            setIsLoading(false);
          }
        });
    }
    // For non-priority images, use Intersection Observer
    else if ('IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && imageRef.current) {
              preloadImage(src)
                .then(() => {
                  imageRef.current!.src = getOptimizedSrc(currentSrc);
                  setIsLoading(false);
                  onLoad?.();
                })
                .catch(handleError);
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '50px 0px',
          threshold: 0.1
        }
      );

      if (imageRef.current) {
        observerRef.current.observe(imageRef.current);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, priority, currentSrc]);

  const handleError = () => {
    if (currentSrc === src) {
      const altSrc = src.startsWith('/') ? src.substring(1) : `/${src}`;
      setCurrentSrc(altSrc);
    } else {
      setError(true);
      setIsLoading(false);
    }
  };

  // Generate optimized URL based on source
  const getOptimizedSrc = (url: string) => {
    if (url.includes('unsplash.com')) {
      return `${url}${url.includes('?') ? '&' : '?'}auto=format,compress${
        width ? `&w=${width}` : ''
      }${height ? `&h=${height}` : ''}&q=${quality}`;
    }
    if (url.includes('firebasestorage.googleapis.com')) {
      // Add Firebase Storage transformations if available in your plan
      return url;
    }
    return url;
  };

  const optimizedSrc = getOptimizedSrc(currentSrc);

  return (
    <div className={`relative ${className}`}>
      {/* Loading Placeholder */}
      {isLoading && !error && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="relative">
            <TreePine className="h-8 w-8 text-primary animate-pulse" />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary/20 rounded-full animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Error Fallback */}
      {error ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <TreePine className="h-8 w-8 text-gray-400" />
        </div>
      ) : (
        <img
          ref={imageRef}
          src={priority || isImageCached(src) ? optimizedSrc : undefined}
          data-src={optimizedSrc}
          alt={alt}
          className={`${className} ${
            isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'
          }`}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          onError={handleError}
          onLoad={() => {
            setIsLoading(false);
            onLoad?.();
          }}
        />
      )}
    </div>
  );
}